/**
 * Apertum chain helpers for WalletConnect writes.
 *
 * WalletConnect pairs on Avalanche C-Chain (43114) so Tangem/Rainbow can connect,
 * then we switch to Apertum (2786). @walletconnect/ethereum-provider still routes
 * every request() — including eth_sendTransaction — through this.chainId, which
 * stays on 43114 unless we pin it. That makes wallets (Xova included) ask for AVAX
 * gas while the user has APTM on Apertum.
 */

export const APERTUM_GAS_NOT_AVAX_MESSAGE =
  'This wallet is on Avalanche (AVAX). Switch to Apertum (chain 2786) — gas is APTM, not AVAX.';

type WalletConnectLike = {
  isWalletConnect?: boolean;
  chainId?: number;
  setDefaultChain?: (chain: string, rpcUrl?: string) => void;
  signer?: {
    setDefaultChain?: (chain: string, rpcUrl?: string) => void;
  };
};

function errorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: unknown }).message ?? '');
  }
  return String(err ?? '');
}

/** True when the provider is a WalletConnect EthereumProvider-like object. */
export function isWalletConnectProvider(provider: unknown): boolean {
  if (!provider || typeof provider !== 'object') return false;
  const p = provider as WalletConnectLike;
  return (
    p.isWalletConnect === true ||
    typeof p.setDefaultChain === 'function' ||
    typeof p.signer?.setDefaultChain === 'function'
  );
}

/**
 * Point a WalletConnect session at Apertum so later eth_sendTransaction
 * uses eip155:2786 instead of the Avalanche pair chain.
 * Returns true when the provider looked like WC and was updated.
 */
export function pinWalletConnectToApertum(
  provider: unknown,
  chainId: number,
  rpcUrl: string
): boolean {
  if (!isWalletConnectProvider(provider)) return false;
  const p = provider as WalletConnectLike;
  const caip = `eip155:${chainId}`;
  try {
    if (typeof p.chainId === 'number') {
      p.chainId = chainId;
    }
    p.setDefaultChain?.(caip, rpcUrl);
    p.signer?.setDefaultChain?.(caip, rpcUrl);
    return true;
  } catch {
    return false;
  }
}

/** Wallet/RPC said AVAX (or C-Chain 43114) when the user needed APTM on Apertum. */
export function isWrongChainGasError(err: unknown): boolean {
  const text = errorText(err).toLowerCase();
  if (!text) return false;
  if (text.includes('avax') && (text.includes('gas') || text.includes('insufficient'))) {
    return true;
  }
  if (text.includes('insufficient funds') && (text.includes('43114') || text.includes('0xa86a'))) {
    return true;
  }
  return false;
}

export function mapApertumWriteError(err: unknown, fallback = 'Transaction failed'): string {
  if (isWrongChainGasError(err)) return APERTUM_GAS_NOT_AVAX_MESSAGE;
  const text = errorText(err).trim();
  return text || fallback;
}
